import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import { updateSyncSettings } from "~/lib/chat/local-mutations";
import { useLocalSyncSettings, useLocalSyncStats } from "~/lib/chat/local-queries";
import { getStorageQuota } from "~/lib/db/local-db";
import { toLocalDbError } from "~/lib/db/local-errors";
import { useOnlineStatus } from "~/lib/network/online";
import { useSyncAllChats } from "~/lib/sync/sync-manager";

export const Route = createFileRoute("/(authenticated)/dashboard/settings/sync/")({
  component: SyncSettingsPage,
});

type StorageQuota = NonNullable<Awaited<ReturnType<typeof getStorageQuota>>>;

function SyncSettingsPage() {
  const settings = useLocalSyncSettings();
  const stats = useLocalSyncStats();
  const { syncAll, status, progress, error, reset } = useSyncAllChats();
  const [quota, setQuota] = useState<StorageQuota | null>(null);
  const [isToggling, setIsToggling] = useState(false);
  const isOnline = useOnlineStatus();

  useEffect(() => {
    let active = true;

    getStorageQuota()
      .then((q) => {
        if (!active) return;
        setQuota(q);
      })
      .catch(() => {
        if (!active) return;
        setQuota(null);
      });

    return () => {
      active = false;
    };
  }, []);

  const handleToggleAutoSync = async () => {
    const nextEnabled = !settings.autoSyncEnabled;
    setIsToggling(true);
    try {
      await updateSyncSettings({ autoSyncEnabled: nextEnabled });
      toast.success(nextEnabled ? "Auto-sync enabled" : "Auto-sync disabled");

      if (nextEnabled) {
        reset();
        const result = await syncAll();
        if (result.success) {
          toast.success(`Synced ${result.syncedCount} chats to cloud`);
        } else {
          toast.error("Batch sync failed", {
            description:
              result.failedCount > 0
                ? `${result.failedCount} chats failed to sync`
                : "Unknown error",
          });
        }
      }
    } catch (err) {
      const localError = toLocalDbError(err);
      toast.error("Failed to update sync settings", {
        description: localError.message,
      });
    } finally {
      setIsToggling(false);
    }
  };

  const handleSyncAll = async () => {
    reset();
    const result = await syncAll();

    if (result.success) {
      toast.success(`Synced ${result.syncedCount} chats to cloud`);
      try {
        await updateSyncSettings({ lastSyncedAt: new Date() });
      } catch (err) {
        const localError = toLocalDbError(err);
        toast.error("Synced, but failed to update local settings", {
          description: localError.message,
        });
      }
    } else {
      toast.error("Batch sync failed", {
        description:
          result.failedCount > 0
            ? `${result.failedCount} chats failed to sync`
            : "Unknown error",
      });
    }
  };

  const lastSyncedLabel = settings.lastSyncedAt
    ? settings.lastSyncedAt.toLocaleString()
    : "Never";

  const quotaLabel = quota
    ? `${Math.round(quota.percentage)}% (${formatBytes(quota.usage)} / ${formatBytes(
        quota.quota,
      )})`
    : "Unavailable";

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold">Sync</h2>
            <p className="text-muted-foreground">Local-first with optional cloud sync</p>
          </div>
          <Button
            variant="outline"
            render={(props) => (
              <Link {...props} to="/dashboard/settings">
                Back
              </Link>
            )}
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label>Chats</Label>
              <p className="text-muted-foreground text-sm">
                {stats.synced} synced · {stats.local} local-only · {stats.total} total
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label>Network</Label>
              <p className="text-muted-foreground text-sm">
                {isOnline ? "Online" : "Offline"}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label>Local storage</Label>
              <p className="text-muted-foreground text-sm">{quotaLabel}</p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label>Last synced</Label>
              <p className="text-muted-foreground text-sm">{lastSyncedLabel}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Controls</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label>Auto-sync</Label>
              <p className="text-muted-foreground text-sm">
                Store locally by default; optionally sync in the background.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleToggleAutoSync}
              disabled={isToggling}
            >
              {settings.autoSyncEnabled ? "Disable" : "Enable"}
            </Button>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <Label>Sync all</Label>
              <p className="text-muted-foreground text-sm">
                Upload all local-only chats to cloud.
              </p>
              {status === "syncing" && progress.total > 0 && (
                <p className="text-muted-foreground mt-1 text-xs">
                  Syncing… {progress.synced} ok · {progress.failed} failed ·{" "}
                  {progress.total} total
                </p>
              )}
              {status === "error" && error && (
                <p className="text-destructive mt-1 text-xs">{error}</p>
              )}
            </div>
            <Button
              onClick={handleSyncAll}
              disabled={status === "syncing" || stats.local === 0}
            >
              Sync all
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
}
